import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CardMedia,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Chip,
  CircularProgress,
  Snackbar,
  Alert,
  Tooltip,
  Divider,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import GroupsIcon from '@mui/icons-material/Groups';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import PriceChangeIcon from '@mui/icons-material/PriceChange';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CommunityPricingDialog from './CommunityPricingDialog';

const CommunitiesView = ({
  venueGroups,
  availableGroups,
  isLoading,
  isAdding,
  isRemoving,
  searchQuery,
  showAddDialog,
  snackbar,
  onAddGroup,
  onRemoveGroup,
  onOpenAddDialog,
  onCloseAddDialog,
  onSearchChange,
  onCloseSnackbar,
  onPricingSuccess,
  venueSlug,
}) => {
  const navigate = useNavigate();
  const [pricingDialogOpen, setPricingDialogOpen] = useState(false);
  const [selectedGroupForPricing, setSelectedGroupForPricing] = useState(null);

  const handleOpenPricingDialog = (group) => {
    setSelectedGroupForPricing(group);
    setPricingDialogOpen(true);
  };

  const handleClosePricingDialog = () => {
    setPricingDialogOpen(false);
    setSelectedGroupForPricing(null);
  };

  const handlePricingSuccess = (message) => {
    if (onPricingSuccess) onPricingSuccess(message);
  };

  return (
    <Box>
      {/* Header Section */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h6" fontWeight="bold">
            Community Pricing
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Associate communities with your venue to offer special pricing to their members
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={onOpenAddDialog}
          sx={{ borderRadius: 2 }}
        >
          Add Community
        </Button>
      </Box>

      {/* Info Card */}
      <Card sx={{ mb: 3, bgcolor: 'primary.50', border: '1px solid', borderColor: 'primary.200' }}>
        <CardContent sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          <LocalOfferIcon color="primary" sx={{ mt: 0.5 }} />
          <Box>
            <Typography variant="subtitle1" fontWeight="medium" color="primary.main">
              How Community Pricing Works
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              When you add a community to your venue, you can create special tariffs for that community's members.
              Members of multiple communities will automatically see the lowest available price.
              Go to the <strong>Catalogue</strong> tab to create community-specific tariffs.
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {/* Loading State */}
      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Empty State */}
      {!isLoading && venueGroups.length === 0 && (
        <Card sx={{ p: 4, textAlign: 'center', bgcolor: 'grey.50' }}>
          <GroupsIcon sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No Communities Associated
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Add communities to offer special pricing to their members
          </Typography>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={onOpenAddDialog}
          >
            Add Your First Community
          </Button>
        </Card>
      )}

      {/* Communities List */}
      {!isLoading && venueGroups.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {venueGroups.map((venueGroup) => {
            const group = venueGroup.group;
            const isBeingRemoved = isRemoving === group?.slug;
            
            return (
              <Card key={group?.slug} sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                  <CardMedia
                    component="img"
                    sx={{ width: 80, height: 80, borderRadius: 2, objectFit: 'cover', flexShrink: 0 }}
                    image={group?.thumbnail || group?.image || '/static/images/default-community.png'}
                    alt={group?.name}
                  />
                  <CardContent sx={{ flex: 1, py: 0, pl: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <Typography variant="subtitle1" fontWeight="medium">
                        {group?.name}
                      </Typography>
                      <Chip
                        size="small"
                        label={venueGroup.status === 'AC' ? 'Active' : 'Pending'}
                        color={venueGroup.status === 'AC' ? 'success' : 'warning'}
                        sx={{ height: 20, fontSize: '0.7rem' }}
                      />
                    </Box>
                    <Typography variant="body2" color="text.secondary" noWrap>
                      {group?.description || 'No description'}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                      <GroupsIcon fontSize="small" color="action" />
                      <Typography variant="caption" color="text.secondary">
                        {group?.participants || 0} members
                      </Typography>
                    </Box>
                  </CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 2, flexShrink: 0 }}>
                    <Tooltip title="View community details">
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<OpenInNewIcon />}
                        onClick={() => navigate(`/community/${group?.slug}/manage`)}
                        sx={{ borderRadius: 2 }}
                      >
                        View
                      </Button>
                    </Tooltip>
                    <Tooltip title="Configure pricing">
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<PriceChangeIcon />}
                        onClick={() => handleOpenPricingDialog(group)}
                        sx={{ borderRadius: 2 }}
                      >
                        Pricing
                      </Button>
                    </Tooltip>
                    <Tooltip title="Remove community">
                      <IconButton
                        onClick={() => onRemoveGroup(group?.slug)}
                        disabled={isBeingRemoved}
                        color="error"
                      >
                        {isBeingRemoved ? <CircularProgress size={24} /> : <DeleteIcon />}
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              </Card>
            );
          })}
        </Box>
      )}

      {/* Add Community Dialog */}
      <Dialog
        open={showAddDialog}
        onClose={onCloseAddDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Community</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            placeholder="Search communities..."
            value={searchQuery}
            onChange={onSearchChange}
            sx={{ mt: 1, mb: 2 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
          
          <Divider sx={{ mb: 2 }} />
          
          {availableGroups.length === 0 ? (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                {searchQuery ? 'No communities found' : 'Search for a community to add'}
              </Typography>
            </Box>
          ) : (
            <List sx={{ maxHeight: 400, overflow: 'auto' }}>
              {availableGroups.map((group) => (
                <ListItem
                  key={group.slug}
                  secondaryAction={
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => onAddGroup(group.slug)}
                      disabled={isAdding}
                    >
                      {isAdding ? <CircularProgress size={20} /> : 'Add'}
                    </Button>
                  }
                  sx={{ 
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    mb: 1,
                    '&:hover': { bgcolor: 'action.hover' }
                  }}
                >
                  <ListItemAvatar>
                    <Avatar
                      src={group.thumbnail || group.image}
                      alt={group.name}
                    >
                      <GroupsIcon />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={group.name}
                    secondary={
                      <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <GroupsIcon fontSize="inherit" />
                        {group.participants || 0} members
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onCloseAddDialog}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Pricing Dialog */}
      <CommunityPricingDialog
        open={pricingDialogOpen}
        onClose={handleClosePricingDialog}
        venueSlug={venueSlug}
        group={selectedGroupForPricing}
        onSuccess={handlePricingSuccess}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={onCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={onCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CommunitiesView;
